/**
 * University Integration API Service
 * Handles communication with university enrollment APIs like SUZA
 */

const API_BASE_URL = (process.env.REACT_APP_API_URL || 'http://localhost:8002/api/v1') + '/sis-integration';

export interface University {
  id: number;
  name: string;
  code: string;
  description: string;
  last_sync: string | null;
  last_sync_status: string | null;
  total_programs: number;
  total_enrollments: number;
  api_configured: boolean;
}

export interface CreateUniversityRequest {
  name: string;
  code: string;
  api_base_url: string;
  enrollment_endpoint: string;
  auth_type: 'none' | 'api_key' | 'bearer' | 'basic';
  api_key?: string;
  contact_email?: string;
  description?: string;
}

export interface UniversityProgram {
  id: number;
  program_code: string;
  program_name: string;
  level: string;
  faculty: string | null;
  department: string | null;
  duration_years: number | null;
  latest_enrollment: {
    academic_year: string | null;
    total_students: number;
    total_male: number;
    total_female: number;
  } | null;
}

export interface UniversityEnrollment {
  id: number;
  academic_year: string;
  level: string;
  program_code: string;
  program_name: string;
  total_students: number;
  total_male: number;
  total_female: number;
  gender_distribution: {
    male_pct: number;
    female_pct: number;
  };
  last_updated: string;
}

export interface EnrollmentSummary {
  university: string;
  academic_year: string | null;
  total_students: number;
  total_male: number;
  total_female: number;
  total_programs: number;
  level_summary: Record<string, {
    total: number;
    male: number;
    female: number;
    programs: number;
  }>;
  gender_distribution: {
    male_pct: number;
    female_pct: number;
  };
}

export interface TrendAnalysis {
  university: string;
  trend_data: Record<string, {
    total: number;
    male: number;
    female: number;
    programs: number;
  }>;
  years_analyzed: string[];
}

export interface SyncLog {
  id: number;
  sync_type: string;
  status: string;
  started_at: string;
  completed_at: string | null;
  duration: string | null;
  records_processed: number;
  records_created: number;
  records_updated: number;
  records_failed: number;
  success_rate: number;
  error_message: string | null;
  api_response_status: number | null;
}

export interface ConnectionTestResult {
  status: 'success' | 'error';
  message: string;
  response_code: number | null;
  data_available: boolean;
}

export interface SyncResult {
  sync_id: number;
  sync_status: string;
  records_processed: number;
  records_created: number;
  records_updated: number;
  records_failed: number;
  success_rate: number;
  duration: string | null;
}

/**
 * Get list of configured universities
 */
export const getUniversities = async (): Promise<{ status: string; data: University[]; total: number }> => {
  const response = await fetch(`${API_BASE_URL}/universities/`, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
    },
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.message || `Failed to fetch universities: ${response.statusText}`);
  }

  return response.json();
};

/**
 * Get university details for editing
 */
export const getUniversityDetails = async (universityId: number): Promise<{ status: string; data: any }> => {
  const response = await fetch(`${API_BASE_URL}/universities/${universityId}/`, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
    },
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.message || `Failed to fetch university details: ${response.statusText}`);
  }

  return response.json();
};

/**
 * Update university configuration
 */
export const updateUniversity = async (universityId: number, data: Partial<CreateUniversityRequest>): Promise<{ status: string; message: string; data: any }> => {
  const response = await fetch(`${API_BASE_URL}/universities/${universityId}/update/`, {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(data),
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.message || `Failed to update university: ${response.statusText}`);
  }

  return response.json();
};

/**
 * Create new university configuration
 */
export const createUniversity = async (data: CreateUniversityRequest): Promise<{ status: string; message: string; data: any }> => {
  const response = await fetch(`${API_BASE_URL}/universities/create/`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(data),
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.message || `Failed to create university: ${response.statusText}`);
  }

  return response.json();
};

/**
 * Test connection to university API
 */
export const testUniversityConnection = async (universityId: number): Promise<{ status: string; data: ConnectionTestResult }> => {
  const response = await fetch(`${API_BASE_URL}/universities/${universityId}/test-connection/`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.message || `Failed to test connection: ${response.statusText}`);
  }

  return response.json();
};

/**
 * Sync enrollment data from university API
 */
export const syncUniversityData = async (universityId: number): Promise<{ status: string; message: string; data: SyncResult }> => {
  const response = await fetch(`${API_BASE_URL}/universities/${universityId}/sync/`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.message || `Failed to sync data: ${response.statusText}`);
  }

  return response.json();
};

/**
 * Get enrollment summary for a university
 */
export const getUniversityEnrollmentSummary = async (
  universityId: number,
  academicYear?: string
): Promise<{ status: string; data: EnrollmentSummary }> => {
  const params = new URLSearchParams();
  if (academicYear) {
    params.append('academic_year', academicYear);
  }

  const response = await fetch(`${API_BASE_URL}/universities/${universityId}/summary/?${params}`, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
    },
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.message || `Failed to fetch enrollment summary: ${response.statusText}`);
  }

  return response.json();
};

/**
 * Get enrollment trends for a university
 */
export const getUniversityTrends = async (
  universityId: number,
  years?: string[]
): Promise<{ status: string; data: TrendAnalysis }> => {
  const params = new URLSearchParams();
  if (years && years.length > 0) {
    years.forEach(year => params.append('years', year));
  }

  const response = await fetch(`${API_BASE_URL}/universities/${universityId}/trends/?${params}`, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
    },
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.message || `Failed to fetch trends: ${response.statusText}`);
  }

  return response.json();
};

/**
 * Get programs for a university
 */
export const getUniversityPrograms = async (
  universityId: number,
  level?: string
): Promise<{ status: string; data: UniversityProgram[]; total: number }> => {
  const params = new URLSearchParams();
  if (level) {
    params.append('level', level);
  }

  const response = await fetch(`${API_BASE_URL}/universities/${universityId}/programs/?${params}`, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
    },
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.message || `Failed to fetch programs: ${response.statusText}`);
  }

  return response.json();
};

/**
 * Get detailed enrollment data
 */
export const getEnrollmentData = async (
  universityId: number,
  filters?: {
    academic_year?: string;
    level?: string;
    program_code?: string;
  }
): Promise<{ status: string; data: UniversityEnrollment[]; total: number; filters: any }> => {
  const params = new URLSearchParams();
  if (filters?.academic_year) params.append('academic_year', filters.academic_year);
  if (filters?.level) params.append('level', filters.level);
  if (filters?.program_code) params.append('program_code', filters.program_code);

  const response = await fetch(`${API_BASE_URL}/universities/${universityId}/enrollments/?${params}`, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
    },
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.message || `Failed to fetch enrollment data: ${response.statusText}`);
  }

  return response.json();
};

/**
 * Get sync history for a university
 */
export const getSyncHistory = async (universityId: number): Promise<{ status: string; data: SyncLog[]; total: number }> => {
  const response = await fetch(`${API_BASE_URL}/universities/${universityId}/sync-history/`, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
    },
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.message || `Failed to fetch sync history: ${response.statusText}`);
  }

  return response.json();
};

/**
 * Validation helper for university creation
 */
export const validateUniversityData = (data: CreateUniversityRequest): string[] => {
  const errors: string[] = [];

  if (!data.name || data.name.trim().length < 3) {
    errors.push('University name must be at least 3 characters long');
  }

  if (!data.code || data.code.trim().length < 2) {
    errors.push('University code must be at least 2 characters long');
  }

  if (!data.api_base_url || !isValidUrl(data.api_base_url)) {
    errors.push('Valid API base URL is required');
  }

  if (!data.enrollment_endpoint || data.enrollment_endpoint.trim().length === 0) {
    errors.push('Enrollment endpoint is required');
  }

  if (data.auth_type !== 'none' && (!data.api_key || data.api_key.trim().length === 0)) {
    errors.push('API key is required when authentication is enabled');
  }

  if (data.contact_email && !isValidEmail(data.contact_email)) {
    errors.push('Valid email address is required');
  }

  return errors;
};

/**
 * Helper function to validate URL format
 */
const isValidUrl = (url: string): boolean => {
  try {
    new URL(url);
    return true;
  } catch {
    return false;
  }
};

/**
 * Helper function to validate email format
 */
const isValidEmail = (email: string): boolean => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
};

/**
 * Format numbers for display
 */
export const formatNumber = (value: number): string => {
  if (value >= 1000000) {
    return `${(value / 1000000).toFixed(1)}M`;
  } else if (value >= 1000) {
    return `${(value / 1000).toFixed(1)}K`;
  }
  return value.toLocaleString();
};

/**
 * Get level color for consistent UI display
 */
export const getLevelColor = (level: string): string => {
  const levelColors: Record<string, string> = {
    'certificate': '#ff9800',
    'diploma': '#2196f3',
    'bachelor': '#4caf50',
    'master': '#9c27b0',
    'phd': '#f44336',
  };
  
  return levelColors[level.toLowerCase()] || '#666666';
};

export default {
  getUniversities,
  getUniversityDetails,
  createUniversity,
  updateUniversity,
  testUniversityConnection,
  syncUniversityData,
  getUniversityEnrollmentSummary,
  getUniversityTrends,
  getUniversityPrograms,
  getEnrollmentData,
  getSyncHistory,
  validateUniversityData,
  formatNumber,
  getLevelColor,
};